import {
  Grid, InputLabel,
  MenuItem,
  Paper, Select, TextField, Typography
} from "@mui/material";
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import produce from 'immer';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { v4 } from 'uuid';
import Application from '../../../Classes/Application';
import { useBusinessRulesEngine } from '../../../Data/BusinessRulesData';
import { useEmployeeRolePermissions } from '../../../Data/EmployeeRoleData';
import { AppState } from '../../../store/rootReducer';
import { IAppCustomer } from '../../../Interfaces/Application/IAppCustomer';
import { IApplication } from '../../../Interfaces/Application/IApplication';
import { ICustomField } from '../../../Interfaces/Common/ICustomField';
import { IMaritalStatus } from '../../../Interfaces/Common/IMaritalStatus';
import { IState } from '../../../Interfaces/Common/IState';
import { ICustomer } from '../../../Interfaces/Customer/ICustomer';
import { ICustomerAddress } from '../../../Interfaces/Customer/ICustomerAddress';
import { ICustomerEmail } from '../../../Interfaces/Customer/ICustomerEmail';
import { ICustomerIncomeDebt } from "../../../Interfaces/Customer/ICustomerIncomeDebt";
import { ICustomerPhone } from '../../../Interfaces/Customer/ICustomerPhone';
import { ICustomerRole } from '../../../Interfaces/Customer/ICustomerRole';
import { IHousingStatus } from '../../../Interfaces/Customer/IHousingStatus';
import { useDialog } from '../../../providers/DialogProvider';
import { updateEditingApp } from "../../../store/application/actions";
import { ApplicationDispatch, SET_SERVER_MESSAGE } from '../../../store/application/types';
import { generateCustomer } from "../../../utils/initializers";
import AddressDialog from '../../Dialogs/AddressDialog';
import { DatePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import StateSelector from "../../StateSelector";
import AddressCard from './AddressCard';
import EmailCard from './EmailCard';
import PhoneNumberCard from './PhoneNumberCard';
import ErrorDialog from "../../Dialogs/ErrorDialog";
import {SSNInput, hasValue, isValidSocialSecurityNumber, LookupItem, DatePickerWithYearSanitizer} from "@amsanalytics/react-shared-library";
import styles from "./_buyer-information-form.module.scss";
import { Constants } from "../../../utils/constants";
import { removeHyphensFromString } from "../../../utils/utils";

interface BuyerInformationProps {
  customerRole: ICustomerRole;
  isReadOnly: boolean;
  disabled?: boolean;
  /** Disables non-name fields only (address, phone, SSN, DOB, employment, etc.) for lock enforcement. Customer names remain editable. */
  disabledForLock?: boolean;
}

const BuyerInformationForm: React.FC<BuyerInformationProps> = (props) => {
  const disabledNonName = props.disabled || props.disabledForLock; // lock applies to non-name fields only
  const currentApp: IApplication = useSelector((state: AppState) => state.applications.editingApp!);
  const customFields: ICustomField[] = useSelector((state: AppState) => state.applications.customFields);
  const dispatch = useDispatch<ApplicationDispatch>();
  const [customer, setCustomer] = useState<ICustomer | undefined>(undefined);
  const [incomeDebt, setIncomeDebt] = useState<ICustomerIncomeDebt | undefined | null>(undefined);
  const [app, setApp] = useState<Application | undefined>(undefined);
  const housingStatuses: IHousingStatus[] = useSelector((state: AppState) => state.applications.housingStatuses.sort((first, second) => first.sortOrder - second.sortOrder));
  const maritalStatuses: IMaritalStatus[] = useSelector((state: AppState) => state.applications.maritalStatuses.sort((first, second) => first.sortOrder - second.sortOrder));
  // list of IDs to filter the list of applicant addresses to show in the dropdown
  const applicantAddressStateIdsToFilterBy = useSelector((appState: AppState) => appState.applications.lookups.applicantAddressStateIdsToFilterBy);
  const customerRoles: ICustomerRole[] = useSelector((state: AppState) => state.applications.customerRoles);
  const states: IState[] = useSelector((state: AppState) => state.applications.states);
  const businessRulesEngine = useBusinessRulesEngine();
  const [openDialog, closeDialog] = useDialog();
  const relationshipToApplicant: LookupItem[] = useSelector((state: AppState) => state.applications.lookups.relationshipToApplicants);
  const identificationTypes: LookupItem[] = useSelector((state: AppState) => state.applications.lookups.identificationTypes);

  useEffect(() => {
    // extract the current primary applicant when the current editing app changes.
    if (currentApp) {
      let appCustomer: IAppCustomer | undefined = currentApp.appCustomers.find(x => x.customerRole.id === props.customerRole.id);
      if (!appCustomer) {
        const initialState: IState = states.find(x => x.abbreviation === "GA")!;
        const initialHousingStatus: IHousingStatus = housingStatuses.find(x => !x.deleted)!;
        const cust: ICustomer = generateCustomer(initialState, initialHousingStatus);
        appCustomer = {
          id: v4(),
          applicationId: currentApp.id,
          customerId: cust.id,
          customer: cust,
          deleted: false,
          createdDate: new Date(),
          customerRole: props.customerRole,
          customerRoleId: props.customerRole.id,
          appCustomerVehicleInsurances: [],
        }
        currentApp.appCustomers = currentApp.appCustomers.concat(appCustomer);
        setCustomer(cust);
        dispatch(updateEditingApp(currentApp, businessRulesEngine));
      } else {
        const cust: ICustomer = appCustomer.customer;
        setCustomer(cust);
        setApp(new Application(currentApp, customFields));
        setIncomeDebt(cust.customerIncomeDebt[0]!);
      }
    }
  }, [currentApp, customFields, customerRoles, dispatch, housingStatuses, props.customerRole, states])

  useEffect(() => {
    if (app) {
      //setDependents(app.getCustomFieldValue("Dependents"));
    }
  }, [app]);

  const handleDateChange = (date: Date, object: any, key: string, canEdit: boolean) => {
    if (canEdit) {
      if (date) {
        object[key] = date;
        dispatch(updateEditingApp(currentApp, businessRulesEngine));
      }
    } else {
      dispatch({ type: SET_SERVER_MESSAGE, payload: { message: 'Current User Role is not allowed to edit this field.', isError: false } });
    }
  }

  // const updateCustomField = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, key: string) => {
  //   app?.setCustomFieldValue(key, e.currentTarget.value);
  //   dispatch(updateEditingApp(currentApp));
  // }

  // const handleObjectChange = <T extends {} | null>(value: T, object: any, fieldName: string, canEdit: boolean): void => {
  //   if (canEdit) {
  //     object[fieldName] = value;
  //     dispatch(updateEditingApp(currentApp, businessRulesEngine));
  //   } else {
  //     dispatch({ type: SET_SERVER_MESSAGE, payload: { message: 'Current User Role is not allowed to edit this field.', isError: false } });
  //   }
  // }

  const handleUpdateEditingApp = (editingApp: IApplication, canEdit: boolean, updateFunction: (editingApp: IApplication) => void) => {
    if (canEdit) {
      const result = produce(editingApp, updateFunction);
      dispatch(updateEditingApp(result, businessRulesEngine));
    } else {
      dispatch({ type: SET_SERVER_MESSAGE, payload: { message: 'Current User Role is not allowed to edit this field.', isError: false } });
    }
  }

  const addAddress = (address: ICustomerAddress): void => {
    const updatedApp = produce(currentApp, currentApp => {
      const ac = currentApp.appCustomers.find(ac => ac.customerId === customer!.id);
      if (ac) {
        if (address.default) {
          // Make sure all other addresses mark default as false.
          ac.customer.customerAddress.forEach(ca => ca.default = false);
        }
        ac.customer.customerAddress = [...ac.customer.customerAddress, address];
      }
    });
    dispatch(updateEditingApp(updatedApp, businessRulesEngine));
  }
  const updateAddress = (address: ICustomerAddress): void => {
    const index: number = customer?.customerAddress.findIndex(x => x.id === address.id)!;
    const numDefaultAddresses = customer?.customerAddress.filter(ca => ca.default)?.length ?? 0;

    // If they are switching the address to not be the current address and there no others set as current, it gives the error dialog
    if (customer?.customerAddress[index].default && !address.default && (numDefaultAddresses < 2)) {
      const errors = ["You must have one current address for each applicant"]
      openDialog({
        children: (
          <ErrorDialog
            title="Applicant Address Errors"
            errors={errors}
            closeDialog={closeDialog}
          />
        ),
      });
    } else {
      const updatedApp = produce(currentApp, currentApp => {
        const updatedCustomer = currentApp.appCustomers.find(ac => ac.customerId === customer!.id)?.customer;
        if (updatedCustomer) {
          updatedCustomer.customerAddress[index] = { ...address };
          // Can only have one primary employment
          if (address.default) {
            updatedCustomer.customerAddress
              .filter(ca => ca.id !== address!.id)
              .forEach(ca => ca.default = false);
          }
        }
      });
      dispatch(updateEditingApp(updatedApp, businessRulesEngine));
    }
  }

  const updatePhone = (phone: ICustomerPhone): void => {
    handleUpdateEditingApp(currentApp, true, (currentApp: IApplication) => {
      if (customer && customer.customerPhone) {
        // Get the cust from the proxy that immer will pass into this function
        const cust = currentApp.appCustomers.find(ac => ac.customerId === customer!.id)?.customer;
        if (cust) {
          const index: number = cust.customerPhone.findIndex(x => x.id === phone.id);
          cust.customerPhone[index] = {
            ...phone
          }
        }
      }
    });
  }

  const updateEmail = (email: ICustomerEmail): void => {
    handleUpdateEditingApp(currentApp, true, (currentApp: IApplication) => {
      if (customer && customer.customerEmail) {
        // Get the cust from the proxy that immer will pass into this function
        const cust = currentApp.appCustomers.find(ac => ac.customerId === customer!.id)?.customer;
        if (cust) {
          const index: number = cust.customerEmail.findIndex(x => x.id === email.id);
          cust.customerEmail[index] = {
            ...email
          }
        }
      }
    })
  }

  const onAddAddress = (): void => {
    openDialog({
      children: (
        <AddressDialog
          accept={(address: ICustomerAddress) => {
            address.customerId = customer!.id;
            addAddress(address);
            closeDialog();
          }}
          closeDialog={closeDialog}
          applicantAddressStateIdsToFilterBy={applicantAddressStateIdsToFilterBy}
          applicationDate={currentApp.applicationDate}
        />
      )
    })
  }

  // const updateReference = (refer: ICustomerReference): void => {
  //   const index: number = customer?.customerReference.findIndex(x => x.id === refer.id)!;
  //   customer!.customerReference![index] = { ...refer };
  //   dispatch(updateEditingApp(currentApp));
  // }

  const [canView, canEdit] = useEmployeeRolePermissions();

  return (
    <div className={props.isReadOnly ? styles.buyerInformationContainerDisabled : styles.buyerInformationContainer}>
      {app && customer && incomeDebt ? (
        <>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              <TextField
                onChange={(e) =>
                  handleUpdateEditingApp(
                    currentApp,
                    canEdit("applicant", "firstName"),
                    (currentApp: IApplication) => {
                      const cust = currentApp.appCustomers.find(
                        (ac) => ac.customer.id === customer.id
                      );
                      if (cust) {
                        cust.customer.firstName = e.target.value;
                      }
                    }
                  )
                }
                value={
                  customer.firstName
                  // canView("applicant", "firstName")
                  //   ? customer.firstName
                  //   : hiddenText
                }
                label="First Name"
                required
                fullWidth
                disabled={props.disabled}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                onChange={(e) =>
                  handleUpdateEditingApp(
                    currentApp,
                    canEdit("applicant", "middleName"),
                    (currentApp: IApplication) => {
                      const cust = currentApp.appCustomers.find(
                        (ac) => ac.customer.id === customer.id
                      );
                      if (cust) {
                        cust.customer.middleName = e.target.value;
                      }
                    }
                  )
                }
                value={
                  // canView("applicant", "middleName")
                  //   ? customer.middleName
                  //   : hiddenText
                  customer.middleName
                }
                label="Middle Name"
                fullWidth
                disabled={props.disabled}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                onChange={(e) =>
                  handleUpdateEditingApp(
                    currentApp,
                    canEdit("applicant", "lastName"),
                    (currentApp: IApplication) => {
                      const cust = currentApp.appCustomers.find(
                        (ac) => ac.customer.id === customer.id
                      );
                      if (cust) {
                        cust.customer.lastName = e.target.value;
                      }
                    }
                  )
                }
                value={
                  // canView("applicant", "lastName")
                  //   ? customer.lastName
                  //   : hiddenText
                  customer.lastName
                }
                label="Last Name"
                fullWidth
                disabled={props.disabled}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Suffix"
                select={canView("applicant", "suffix")}
                placeholder="Suffix"
                value={
                  customer.suffix || ""
                }
                style={{ minWidth: '165px' }}
                onChange={(e) =>
                  handleUpdateEditingApp(
                    currentApp,
                    canEdit("applicant", "suffix"),
                    (currentApp: IApplication) => {
                      const cust = currentApp.appCustomers.find(
                        (ac) => ac.customer.id === customer.id
                      );
                      if (cust) {
                        cust.customer.suffix = e.target.value;
                      }
                    }
                  )
                }
                disabled={props.disabled}
              >
                <MenuItem>&nbsp;</MenuItem>
                <MenuItem value="Jr.">Jr.</MenuItem>
                <MenuItem value="Sr.">Sr.</MenuItem>
                <MenuItem value="I">I</MenuItem>
                <MenuItem value="II">II</MenuItem>
                <MenuItem value="III">III</MenuItem>
                <MenuItem value="IV">IV</MenuItem>
                <MenuItem value="V">V</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              {!disabledNonName && true ? (
                <SSNInput
                  label="SSN"
                  value={customer.ssn}
                  style={{ backgroundColor: 'white', width: '100%' }}
                  error={hasValue(customer.ssn) && !isValidSocialSecurityNumber(removeHyphensFromString(customer.ssn))}
                  valid={hasValue(customer.ssn) && isValidSocialSecurityNumber(removeHyphensFromString(customer.ssn))}
                  validationMessage="SSN is empty or invalid."
                  onChangeValue={(val) =>
                    handleUpdateEditingApp(
                      currentApp,
                      canEdit("applicant", "ssn"),
                      (currentApp: IApplication) => {
                        const cust = currentApp.appCustomers.find(
                          (ac) => ac.customer.id === customer.id
                        );
                        if (cust) {
                          cust.customer.ssn = val;
                        }
                      }
                    )
                  }
                />
              ) : (
                <TextField label="SSN" value="***-**-****" />
              )}
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                value={
                  // canView("applicant", "dlnumber")
                  //   ? customer.dlnumber
                  //   : hiddenText
                  customer.dlnumber
                }
                onChange={(e) =>
                  handleUpdateEditingApp(
                    currentApp,
                    canEdit("applicant", "dlnumber"),
                    (currentApp: IApplication) => {
                      const cust = currentApp.appCustomers.find(
                        (ac) => ac.customer.id === customer.id
                      );
                      if (cust) {
                        cust.customer.dlnumber = e.currentTarget.value;
                      }
                    }
                  )
                }
                label="ID Number"
                disabled={disabledNonName}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <InputLabel >{"Identification Type"}</InputLabel>
              <Select
                name="identificationType"
                disabled={disabledNonName}
                variant='standard'
                fullWidth
                style={{ minWidth: "165px" }}
                value={customer.identificationTypeId ?? null}
                onChange={(e) =>
                  handleUpdateEditingApp(
                    currentApp,
                    canEdit("applicant", "dlnumber"),
                    (currentApp: IApplication) => {
                      const cust = currentApp.appCustomers.find(
                        (ac) => ac.customer.id === customer.id
                      );
                      if (cust) {
                        cust.customer.identificationTypeId = e.target.value;
                      }
                    }
                  )
                }
              >
                <MenuItem>&nbsp;</MenuItem>
                {identificationTypes.map((x, key) => (
                  <MenuItem key={key} value={x.id}>
                    {x.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={6} sm={3}>
              {/* {canView("applicant", "dlstateId") ? ( */}
              <StateSelector
                labelText={'DL State'}
                showLabel
                value={customer.dlstateId}
                onChange={(val) =>
                  handleUpdateEditingApp(
                    currentApp,
                    canEdit("applicant", "dlstateId"),
                    (currentApp: IApplication) => {
                      const cust = currentApp.appCustomers.find(
                        (ac) => ac.customer.id === customer.id
                      );
                      if (cust) {
                        cust.customer.dlstateId = val;
                      }
                    }
                  )
                }
                DefaultStateId={customer.dlstateId}
                disabled={disabledNonName}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Housing Status"
                fullWidth
                disabled={disabledNonName}
                style={{ minWidth: '165px' }}
                select={true}
                value={customer.housingStatusId || ""}
                onChange={(e) =>
                  handleUpdateEditingApp(
                    currentApp,
                    canEdit("applicant", "housingStatusId"),
                    (currentApp: IApplication) => {
                      const cust = currentApp.appCustomers.find(
                        (ac) => ac.customer.id === customer.id
                      );
                      if (cust) {
                        cust.customer.housingStatusId = e.target.value;
                      }
                    }
                  )
                }
              >
                <MenuItem>&nbsp;</MenuItem>
                {housingStatuses.map((x, key) => (
                  <MenuItem key={key} value={x.id}>
                    {x.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <div style={{ display: `flex`, flexDirection: `column` }}>
                <Typography
                  variant="caption"
                  style={{ color: `rgba(0, 0, 0, 0.54)`, lineHeight: `1` }}
                >
                  Date of Birth
                </Typography>
                <DatePickerWithYearSanitizer
                  disabled={disabledNonName}
                  value={
                    customer.dateOfBirth ? new Date(customer.dateOfBirth) : null
                  }

                  onChange={(event: DatePickerChangeEvent) => {
                    if (event.value) {
                      handleUpdateEditingApp(
                        currentApp,
                        canEdit("applicant", "dateOfBirth"),
                        (currentApp: IApplication) => {
                          const cust = currentApp.appCustomers.find(
                            (ac) => ac.customer.id === customer.id
                          );
                          if (cust) {
                            cust.customer.dateOfBirth = new Date(
                              event.value!.toISOString()
                            );
                          }
                        }
                      );
                    }
                  }}
                />
              </div>
            </Grid>
            {
              currentApp.appCustomers.find((ac) => ac.customer.id === customer.id)?.customerRoleId?.toUpperCase() !== Constants.roleApplicantId.toUpperCase() && (
                <Grid item xs={6} sm={3}>
                  <TextField
                    disabled={disabledNonName}
                    label="Relationship to Applicant"
                    fullWidth
                    style={{ minWidth: "165px" }}
                    select={true}
                    value={currentApp.appCustomers.find((ac) => ac.customer.id === customer.id)?.relationshipToApplicantId || ''}
                    onChange={(e) =>
                      handleUpdateEditingApp(
                        currentApp,
                        canEdit("applicant", "maritalStatusId"),
                        (currentApp: IApplication) => {
                          const cust = currentApp.appCustomers.find((ac) => ac.customer.id === customer.id);
                          if (cust) {
                            cust.relationshipToApplicantId = e.target.value;
                          }
                        }
                      )
                    }
                  >
                    <MenuItem>&nbsp;</MenuItem>
                    {relationshipToApplicant.map((x, key) => (
                      <MenuItem key={key} value={x.id}>
                        {x.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )
            }
            <Grid item xs={6} sm={3}>
              <TextField
                disabled={disabledNonName}
                type="number"
                value={
                  // canView("applicant", "numberOfDependents")
                  //   ? customer?.numberOfDependents ?? 0
                  //   : "****"
                  customer?.numberOfDependents ?? 0
                }
                onChange={(e) =>
                  handleUpdateEditingApp(
                    currentApp,
                    canEdit("applicant", "numberOfDependents"),
                    (currentApp: IApplication) => {
                      const cust = currentApp.appCustomers.find(
                        (ac) => ac.customer.id === customer.id
                      );
                      if (cust) {
                        cust.customer.numberOfDependents = e.target.value;
                      }
                    }
                  )
                }
                label="# of Dependents"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                disabled={disabledNonName}
                label="Marital Status"
                fullWidth
                style={{ minWidth: "165px" }}
                select={true}
                value={customer.maritalStatusId || ""}
                onChange={(e) =>
                  handleUpdateEditingApp(
                    currentApp,
                    canEdit("applicant", "maritalStatusId"),
                    (currentApp: IApplication) => {
                      const cust = currentApp.appCustomers.find(
                        (ac) => ac.customer.id === customer.id
                      );
                      if (cust) {
                        cust.customer.maritalStatusId = e.target.value;
                      }
                    }
                  )
                }
              >
                <MenuItem>&nbsp;</MenuItem>
                {maritalStatuses.map((x, key) => (
                  <MenuItem key={key} value={x.id}>
                    {x.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item lg={12} xs={12} sm={12}>
              <TextField
                disabled={disabledNonName}
                fullWidth
                value={/*canView("applicant", "note")*/ true ? customer.note : "****"}
                onChange={(e) =>
                  handleUpdateEditingApp(
                    currentApp,
                    canEdit("applicant", "note"),
                    (currentApp: IApplication) => {
                      const cust = currentApp.appCustomers.find(
                        (ac) => ac.customer.id === customer.id
                      );
                      if (cust) {
                        cust.customer.note = e.target.value;
                      }
                    }
                  )
                }
                multiline
                rows={2}
                label="Notes"
              />
            </Grid>
          </Grid>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Typography style={{ margin: 5 }} variant="h6" component="h6">
              Addresses
            </Typography>
            <div style={{ display: "flex", alignItems: "center" }}>
              <IconButton onClick={onAddAddress}>
                <AddIcon />
              </IconButton>
            </div>
          </div>
          {customer.customerAddress.map((c, key) => (
            <AddressCard
              style={key !== 0 ? { marginTop: 15, borderTop: 'solid 1px #aaaaaa' } : {}}
              disabled={disabledNonName}
              key={key}
              address={c}
              onUpdateAddress={updateAddress}
              applicantAddressStateIdsToFilterBy={applicantAddressStateIdsToFilterBy}
              applicationDate={currentApp.applicationDate}
            />
          ))}
          <div style={{ marginTop: 15 }} />
          <Typography style={{ margin: 5 }} variant="h6" component="h6">
            Phone Numbers
          </Typography>
          <Paper>
            {customer.customerPhone.map((p) => (
              <PhoneNumberCard
                disabled={disabledNonName}
                key={p.id}
                phone={p}
                onUpdatePhone={updatePhone}
              />
            ))}
          </Paper>

          <div style={{ marginTop: 15 }} />
          <Typography style={{ margin: 5 }} variant="h6" component="h6">
            Email
          </Typography>
          <Paper>
            {customer.customerEmail.map((e) => (
              <EmailCard disabled={disabledNonName} key={e.id} email={e} onUpdateEmail={updateEmail} />
            ))}
          </Paper>

          {/* <div style={{ marginTop: 15 }} />
          <Paper className={classes.paper}>
            <Typography variant="h6" component="h6">
              Bankruptcy (Special Finance)
          </Typography>
            <Grid container spacing={3}>
              <Grid item xs={6} sm={3}>
                <FormControlLabel
                  control={
                  <Switch checked={canView('customerIncomeDebt', 'bankruptcyFiled') ? incomeDebt.bankruptcyFiled : false} 
                    onChange={(e) => 
                      handleObjectChange<boolean>(e.currentTarget.checked, incomeDebt, "bankruptcyFiled", canEdit("customerIncomeDebt", "bankruptcyFiled"))
                    } 
                  />
                  }
                  label="Bankruptcy Filed?"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                {canView("customerIncomeDebt", "dischargeDate")
                ?
                <DatePickerWithYearSanitizer
                  label="Discharge Date"
                  format="MM/dd/yyyy"
                  value={incomeDebt.dischargeDate ? new Date(incomeDebt.dischargeDate) : null}
                  onChange={(e: DatePickerChangeEvent) => {
                    if (e.value) {
                      handleDateChange(e.value, incomeDebt, "dischargeDate", canEdit("customerIncomeDebt", "dischargeDate"));
                    }
                  }}
                />
                :
                <TextField label="Discharge Date" value={hiddenText} />
                }
              </Grid>
              <Grid item xs={6} sm={6}>

              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth 
                  value={canView("customerIncomeDebt", "priorVehicleLienHolder") ? incomeDebt.priorVehicleLienHolder : hiddenText} 
                  onChange={(e) => 
                    handleObjectChange<string>(e.currentTarget.value, incomeDebt, "priorVehicleLienHolder", canEdit("customerIncomeDebt", "priorVehicleLienHolder"))
                  } 
                  label="Lienholder" 
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                  <Switch 
                    checked={canView("customerIncomeDebt", "tradeVehicle") ? incomeDebt.tradeVehicle : false} 
                    onChange={(e) => 
                      handleObjectChange<boolean>(e.currentTarget.checked, incomeDebt, "tradeVehicle", canEdit("customerIncomeDebt", "tradeVehicle"))
                    } 
                  />
                  }
                  label="Trade Vehicle?"
                />
              </Grid>       
              <Grid item xs={6} sm={3}>
                <TextField type={canView("customerIncomeDebt", "tradeYear") ? "number" : ''} 
                  value={canView("customerIncomeDebt", "tradeYear") ? (incomeDebt.tradeYear ?? "") : hiddenText} 
                  onChange={(e) => 
                    handleObjectChange<number | null>(nullOrNumber(e.currentTarget.value), incomeDebt, "tradeYear", canEdit("customerIncomeDebt", "tradeYear"))
                  } 
                  fullWidth 
                  label="Year" 
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField 
                  fullWidth 
                  value={canView("customerIncomeDebt", "tradeMake") ? incomeDebt.tradeMake : hiddenText} 
                  onChange={(e) => 
                    handleObjectChange<string>(e.currentTarget.value, incomeDebt, "tradeMake", canEdit("customerIncomeDebt", "tradeMake"))
                  } 
                  label="Make" 
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField 
                  fullWidth 
                  value={canView("customerIncomeDebt", "tradeModel") ? incomeDebt.tradeModel : hiddenText} 
                  onChange={(e) => 
                    handleObjectChange<string>(e.currentTarget.value, incomeDebt, "tradeModel", canEdit("customerIncomeDebt", "tradeModel"))
                  } 
                  label="Model" 
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField 
                  value={canView("customerIncomeDebt", "priorVehicleAmountBorrowed") ? incomeDebt.priorVehicleAmountBorrowed : hiddenText} 
                  onChange={(e) => 
                    handleObjectChange<number | null>(nullOrNumber(e.currentTarget.value), incomeDebt, "priorVehicleAmountBorrowed", canEdit("customerIncomeDebt", "priorVehicleAmountBorrowed"))
                  } 
                  label="Loan Amount" 
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <TextField
                  fullWidth
                  value={canView("customerIncomeDebt", "bankruptcyComments") ? (incomeDebt.bankruptcyComments || '') : hiddenText}
                  onChange={(e) => 
                    handleObjectChange<string>(e.currentTarget.value, incomeDebt, "bankruptcyComments", canEdit("customerIncomeDebt", "bankruptcyComments"))
                  }
                  label="Bankruptcy Comments"
                />
              </Grid>
            </Grid>
          </Paper>  */}
        </>
      ) : null}
    </div>
  );
};

export default BuyerInformationForm;
